import { EquipmentItem, ItemMod } from '@shared/types';

// Legendary icon paths
const S7 = 'sprites/items/icons_7soul';
const DB32 = 'sprites/items/db32_items';

// Unique legendary effect types
export type LegendaryEffectType =
  | 'on_hit'           // Triggers when hitting enemy
  | 'on_kill'          // Triggers when killing enemy
  | 'on_damaged'       // Triggers when taking damage
  | 'passive'          // Always active
  | 'on_skill_use'     // Triggers when using a skill
  | 'aura';            // Provides area effect

export interface LegendaryEffect {
  type: LegendaryEffectType;
  effectId: string;
  description: string;
  descriptionKey: string;
  value: number;
  chance?: number;      // Proc chance (0-100)
  cooldown?: number;    // Cooldown in seconds
  duration?: number;    // Duration in seconds
}

export interface LegendaryItem extends EquipmentItem {
  uniqueId: string;           // Unique identifier for this legendary
  flavorText: string;         // Lore text
  flavorTextKey: string;
  legendaryEffects: LegendaryEffect[];
  fixedMods: ItemMod[];       // Always has these mods
  classRestriction?: string[];  // Only these classes can use
}

// ========================
// Legendary Weapons
// ========================

export const LEGENDARY_WEAPONS: LegendaryItem[] = [
  // Alp - Warrior Weapons
  {
    uniqueId: 'legend_bozkirda_inleyen',
    id: '',  // Will be set on creation
    name: 'Bozkırda İnleyen',
    icon: `${S7}/W_Gold_Sword.png`,
    slot: 'weapon',
    level: 30,
    rarity: 'legendary',
    implicit: [{ type: 'damage', value: 45, tier: 5 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'damage', value: 35, tier: 4 },
      { type: 'critChance', value: 15, tier: 3 },
      { type: 'strength', value: 15, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_hit',
        effectId: 'wind_strike',
        description: 'Vuruşlarda %15 şans ile rüzgar dalgası yayar, yakındaki düşmanlara hasar verir.',
        descriptionKey: 'legendary.bozkirda_inleyen.effect1',
        value: 50,  // % of base damage
        chance: 15,
        cooldown: 2,
      },
      {
        type: 'on_kill',
        effectId: 'wolf_howl',
        description: 'Öldürmelerde 3 saniye boyunca saldırı hızı %20 artar.',
        descriptionKey: 'legendary.bozkirda_inleyen.effect2',
        value: 20,
        duration: 3,
      },
    ],
    flavorText: 'Bozkırın rüzgarı gibi sessiz, kurt gibi ölümcül.',
    flavorTextKey: 'legendary.bozkirda_inleyen.flavor',
    classRestriction: ['alp'],
  },
  {
    uniqueId: 'legend_erlik_kilici',
    id: '',
    name: 'Erlik\'in Gazabı',
    icon: `${S7}/W_Sword022.png`,
    slot: 'weapon',
    level: 45,
    rarity: 'legendary',
    implicit: [{ type: 'damage', value: 65, tier: 6 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'damage', value: 50, tier: 5 },
      { type: 'maxHp', value: 80, tier: 4 },
      { type: 'armor', value: 40, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_hit',
        effectId: 'soul_drain',
        description: 'Vuruşlarda %10 şans ile düşmandan can çalar.',
        descriptionKey: 'legendary.erlik_kilici.effect1',
        value: 5,  // % of damage dealt
        chance: 10,
      },
      {
        type: 'passive',
        effectId: 'death_aura',
        description: 'Can %30\'un altına düştüğünde hasar %25 artar.',
        descriptionKey: 'legendary.erlik_kilici.effect2',
        value: 25,
      },
    ],
    flavorText: 'Ölüm alemine en yakın olduğunda, gücün zirvesine ulaşırsın.',
    flavorTextKey: 'legendary.erlik_kilici.flavor',
    classRestriction: ['alp'],
  },

  // Kam - Shaman Weapons
  {
    uniqueId: 'legend_kam_asasi',
    id: '',
    name: 'Ata Ruhlarının Asası',
    icon: `${S7}/W_Staff08.png`,
    slot: 'weapon',
    level: 30,
    rarity: 'legendary',
    implicit: [{ type: 'spellDamage', value: 55, tier: 5 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'spellDamage', value: 40, tier: 4 },
      { type: 'maxMana', value: 60, tier: 4 },
      { type: 'spirit', value: 18, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_skill_use',
        effectId: 'spirit_echo',
        description: 'Beceri kullanımlarında %20 şans ile beceri tekrar aktif olur (mana harcamaz).',
        descriptionKey: 'legendary.kam_asasi.effect1',
        value: 100,
        chance: 20,
        cooldown: 5,
      },
      {
        type: 'passive',
        effectId: 'ancestral_wisdom',
        description: 'Mana yenilenmesi %30 artar.',
        descriptionKey: 'legendary.kam_asasi.effect2',
        value: 30,
      },
    ],
    flavorText: 'Bin yıllık bilgelik tek bir değnekte toplanmış.',
    flavorTextKey: 'legendary.kam_asasi.flavor',
    classRestriction: ['kam'],
  },
  {
    uniqueId: 'legend_gok_tanri_asasi',
    id: '',
    name: 'Gök Tanrı\'nın Işığı',
    icon: `${S7}/W_Staff07.png`,
    slot: 'weapon',
    level: 45,
    rarity: 'legendary',
    implicit: [{ type: 'spellDamage', value: 75, tier: 6 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'spellDamage', value: 55, tier: 5 },
      { type: 'maxMana', value: 100, tier: 5 },
      { type: 'energyShield', value: 50, tier: 4 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_skill_use',
        effectId: 'thunder_strike',
        description: 'Beceri kullanımlarında hedefin üzerine gökten şimşek düşer (ekstra hasar).',
        descriptionKey: 'legendary.gok_tanri_asasi.effect1',
        value: 75,  // % of spell damage as lightning
        chance: 25,
        cooldown: 3,
      },
      {
        type: 'aura',
        effectId: 'divine_protection',
        description: 'Yakındaki müttefiklerin büyü direnci %15 artar.',
        descriptionKey: 'legendary.gok_tanri_asasi.effect2',
        value: 15,
      },
    ],
    flavorText: 'Gökyüzünün gazabını yeryüzüne indirir.',
    flavorTextKey: 'legendary.gok_tanri_asasi.flavor',
    classRestriction: ['kam'],
  },

  // Mergen - Archer Weapons
  {
    uniqueId: 'legend_altin_yay',
    id: '',
    name: 'Altın Kartal Yayı',
    icon: `${S7}/W_Gold_Bow.png`,
    slot: 'weapon',
    level: 30,
    rarity: 'legendary',
    implicit: [
      { type: 'damage', value: 35, tier: 5 },
      { type: 'attackSpeed', value: 25, tier: 4 },
    ],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'damage', value: 25, tier: 3 },
      { type: 'critChance', value: 20, tier: 4 },
      { type: 'agility', value: 15, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_hit',
        effectId: 'eagle_eye',
        description: 'Kritik vuruşlarda ok hedefe takip eder (kaçamaz).',
        descriptionKey: 'legendary.altin_yay.effect1',
        value: 100,
        chance: 100,  // Always on crit
      },
      {
        type: 'passive',
        effectId: 'wind_runner',
        description: 'Hareket hızı %15 artar.',
        descriptionKey: 'legendary.altin_yay.effect2',
        value: 15,
      },
    ],
    flavorText: 'Altın kartal gibi gökyüzünden avına saldırır.',
    flavorTextKey: 'legendary.altin_yay.flavor',
    classRestriction: ['mergen'],
  },
  {
    uniqueId: 'legend_kurt_yay',
    id: '',
    name: 'Börü\'nün Dişi',
    icon: `${S7}/W_Bow17.png`,
    slot: 'weapon',
    level: 45,
    rarity: 'legendary',
    implicit: [
      { type: 'damage', value: 50, tier: 6 },
      { type: 'attackSpeed', value: 35, tier: 5 },
    ],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'damage', value: 40, tier: 4 },
      { type: 'critChance', value: 25, tier: 5 },
      { type: 'moveSpeed', value: 15, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_hit',
        effectId: 'wolf_pack',
        description: 'Vuruşlarda %15 şans ile 2 ekstra ok ateşler.',
        descriptionKey: 'legendary.kurt_yay.effect1',
        value: 2,  // Extra arrows
        chance: 15,
      },
      {
        type: 'on_kill',
        effectId: 'predator_instinct',
        description: 'Öldürmelerde 5 saniye boyunca görünmezlik kazanır.',
        descriptionKey: 'legendary.kurt_yay.effect2',
        value: 5,  // Duration
        cooldown: 15,
      },
    ],
    flavorText: 'Börü sürüsünün ruhu bu yayda yaşar.',
    flavorTextKey: 'legendary.kurt_yay.flavor',
    classRestriction: ['mergen'],
  },
];

// ========================
// Legendary Armor
// ========================

export const LEGENDARY_ARMOR: LegendaryItem[] = [
  // Chest Armor
  {
    uniqueId: 'legend_demir_kalp',
    id: '',
    name: 'Demir Kalp Zırhı',
    icon: `${S7}/A_Armour03.png`,
    slot: 'chest',
    level: 35,
    rarity: 'legendary',
    implicit: [{ type: 'armor', value: 80, tier: 5 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'maxHp', value: 100, tier: 5 },
      { type: 'armor', value: 60, tier: 4 },
      { type: 'allResist', value: 15, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_damaged',
        effectId: 'iron_will',
        description: 'Hasar aldığında %20 şans ile hasarın %50\'si engellenir.',
        descriptionKey: 'legendary.demir_kalp.effect1',
        value: 50,
        chance: 20,
      },
      {
        type: 'passive',
        effectId: 'steadfast',
        description: 'Sersemletme ve itme etkilerine karşı bağışıklık.',
        descriptionKey: 'legendary.demir_kalp.effect2',
        value: 100,
      },
    ],
    flavorText: 'Yüreği demirden olanlar asla yenilmez.',
    flavorTextKey: 'legendary.demir_kalp.flavor',
  },
  {
    uniqueId: 'legend_golge_pelerin',
    id: '',
    name: 'Gölge Pelerini',
    icon: `${S7}/A_Clothing02.png`,
    slot: 'chest',
    level: 35,
    rarity: 'legendary',
    implicit: [{ type: 'evasion', value: 60, tier: 5 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'evasion', value: 45, tier: 4 },
      { type: 'moveSpeed', value: 15, tier: 3 },
      { type: 'agility', value: 12, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_damaged',
        effectId: 'shadow_step',
        description: 'Ölümcül hasar aldığında %30 şans ile hasar sıfırlanır ve kısa mesafe ışınlanır.',
        descriptionKey: 'legendary.golge_pelerin.effect1',
        value: 100,
        chance: 30,
        cooldown: 60,
      },
      {
        type: 'passive',
        effectId: 'shadow_veil',
        description: 'Gece yarısından sonra (oyun içi) kaçınma %20 artar.',
        descriptionKey: 'legendary.golge_pelerin.effect2',
        value: 20,
      },
    ],
    flavorText: 'Gölgeler içinde doğdu, gölgeler içinde kaybolacak.',
    flavorTextKey: 'legendary.golge_pelerin.flavor',
  },

  // Helmet
  {
    uniqueId: 'legend_bilge_kagan_tolga',
    id: '',
    name: 'Bilge Kağan\'ın Tolgası',
    icon: `${S7}/C_Elm04.png`,
    slot: 'head',
    level: 40,
    rarity: 'legendary',
    implicit: [{ type: 'armor', value: 40, tier: 4 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'maxHp', value: 60, tier: 4 },
      { type: 'maxMana', value: 50, tier: 4 },
      { type: 'allResist', value: 12, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'passive',
        effectId: 'wisdom_of_ages',
        description: 'Tecrübe kazanımı %25 artar.',
        descriptionKey: 'legendary.bilge_kagan.effect1',
        value: 25,
      },
      {
        type: 'aura',
        effectId: 'leadership',
        description: 'Yakındaki düşmanlar yavaşlar (%15).',
        descriptionKey: 'legendary.bilge_kagan.effect2',
        value: 15,
      },
    ],
    flavorText: 'Bin yıllık devletin bilgeliği bu tolgada yaşar.',
    flavorTextKey: 'legendary.bilge_kagan.flavor',
  },

  // Boots
  {
    uniqueId: 'legend_ruzgar_cizmesi',
    id: '',
    name: 'Rüzgar Çizmesi',
    icon: `${S7}/A_Shoes06.png`,
    slot: 'boots',
    level: 30,
    rarity: 'legendary',
    implicit: [{ type: 'moveSpeed', value: 20, tier: 4 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'moveSpeed', value: 15, tier: 3 },
      { type: 'evasion', value: 35, tier: 3 },
      { type: 'agility', value: 10, tier: 2 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'passive',
        effectId: 'wind_walk',
        description: 'Hareket ederken yavaş yavaş görünmezleşir (3 saniye sonra tam görünmezlik).',
        descriptionKey: 'legendary.ruzgar_cizmesi.effect1',
        value: 3,  // Seconds to full invisibility
      },
      {
        type: 'on_damaged',
        effectId: 'quick_escape',
        description: 'Hasar aldığında 2 saniye boyunca hareket hızı %40 artar.',
        descriptionKey: 'legendary.ruzgar_cizmesi.effect2',
        value: 40,
        duration: 2,
        cooldown: 10,
      },
    ],
    flavorText: 'Rüzgar gibi gel, rüzgar gibi git.',
    flavorTextKey: 'legendary.ruzgar_cizmesi.flavor',
  },

  // Belt
  {
    uniqueId: 'legend_savas_kemeri',
    id: '',
    name: 'Savaş Lordunun Kemeri',
    icon: `${S7}/A_Armor05.png`,
    slot: 'belt',
    level: 35,
    rarity: 'legendary',
    implicit: [{ type: 'maxHp', value: 50, tier: 4 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'maxHp', value: 75, tier: 4 },
      { type: 'strength', value: 12, tier: 3 },
      { type: 'armor', value: 30, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_kill',
        effectId: 'battle_frenzy',
        description: 'Öldürmelerde can ve mana %5 yenilenir.',
        descriptionKey: 'legendary.savas_kemeri.effect1',
        value: 5,
      },
      {
        type: 'passive',
        effectId: 'war_hardened',
        description: 'Alınan fiziksel hasar %10 azalır.',
        descriptionKey: 'legendary.savas_kemeri.effect2',
        value: 10,
      },
    ],
    flavorText: 'Yüz savaş görmüş, bin düşman yenmiş.',
    flavorTextKey: 'legendary.savas_kemeri.flavor',
  },
];

// ========================
// Legendary Accessories
// ========================

export const LEGENDARY_ACCESSORIES: LegendaryItem[] = [
  // Amulet
  {
    uniqueId: 'legend_umay_muskasi',
    id: '',
    name: 'Umay Ana\'nın Muskası',
    icon: `${S7}/Ac_Necklace07.png`,
    slot: 'amulet',
    level: 25,
    rarity: 'legendary',
    implicit: [{ type: 'allResist', value: 15, tier: 4 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'maxHp', value: 50, tier: 3 },
      { type: 'maxMana', value: 40, tier: 3 },
      { type: 'spirit', value: 10, tier: 2 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'passive',
        effectId: 'mothers_blessing',
        description: 'Can yenilenmesi %50 artar.',
        descriptionKey: 'legendary.umay_muskasi.effect1',
        value: 50,
      },
      {
        type: 'on_damaged',
        effectId: 'divine_shield',
        description: 'Can %20\'nin altına düştüğünde 3 saniyelik kalkan oluşturur.',
        descriptionKey: 'legendary.umay_muskasi.effect2',
        value: 200,  // Shield amount
        duration: 3,
        cooldown: 120,
      },
    ],
    flavorText: 'Doğuran ve koruyan tanrıçanın kutsaması.',
    flavorTextKey: 'legendary.umay_muskasi.flavor',
  },
  {
    uniqueId: 'legend_tengri_gozlugu',
    id: '',
    name: 'Tengri\'nin Gözü',
    icon: `${S7}/Ac_Necklace08.png`,
    slot: 'amulet',
    level: 50,
    rarity: 'legendary',
    implicit: [{ type: 'allResist', value: 25, tier: 5 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'spellDamage', value: 35, tier: 4 },
      { type: 'maxMana', value: 80, tier: 5 },
      { type: 'energyShield', value: 60, tier: 4 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'passive',
        effectId: 'all_seeing',
        description: 'Tüm gizli düşmanlar ve hazineler görünür.',
        descriptionKey: 'legendary.tengri_gozlugu.effect1',
        value: 100,
      },
      {
        type: 'on_skill_use',
        effectId: 'divine_power',
        description: 'Beceriler %20 daha fazla hasar verir ama %10 daha fazla mana harcar.',
        descriptionKey: 'legendary.tengri_gozlugu.effect2',
        value: 20,
      },
    ],
    flavorText: 'Gök Tanrı her şeyi görür, her şeyi bilir.',
    flavorTextKey: 'legendary.tengri_gozlugu.flavor',
  },

  // Ring
  {
    uniqueId: 'legend_alev_yuzuk',
    id: '',
    name: 'Ateş Ruhunun Yüzüğü',
    icon: `${S7}/Ac_Ring02.png`,
    slot: 'ring1',
    level: 30,
    rarity: 'legendary',
    implicit: [],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'damage', value: 20, tier: 3 },
      { type: 'spellDamage', value: 25, tier: 3 },
      { type: 'critChance', value: 10, tier: 2 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_hit',
        effectId: 'burning_touch',
        description: 'Vuruşlarda düşman 3 saniye yanar (saniyede hasar).',
        descriptionKey: 'legendary.alev_yuzuk.effect1',
        value: 15,  // DPS
        duration: 3,
        chance: 30,
      },
      {
        type: 'passive',
        effectId: 'fire_immunity',
        description: 'Ateş hasarına %30 direnç.',
        descriptionKey: 'legendary.alev_yuzuk.effect2',
        value: 30,
      },
    ],
    flavorText: 'Ataşın gücü parmağında, dikkat et yanmayasın.',
    flavorTextKey: 'legendary.alev_yuzuk.flavor',
  },
  {
    uniqueId: 'legend_buz_yuzuk',
    id: '',
    name: 'Kış Ruhunun Yüzüğü',
    icon: `${S7}/Ac_Ring02.png`,
    slot: 'ring1',
    level: 30,
    rarity: 'legendary',
    implicit: [],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'damage', value: 18, tier: 3 },
      { type: 'maxMana', value: 35, tier: 3 },
      { type: 'energyShield', value: 25, tier: 2 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_hit',
        effectId: 'frost_touch',
        description: 'Vuruşlarda %25 şans ile düşman 2 saniye yavaşlar.',
        descriptionKey: 'legendary.buz_yuzuk.effect1',
        value: 40,  // % slow
        duration: 2,
        chance: 25,
      },
      {
        type: 'passive',
        effectId: 'frost_immunity',
        description: 'Buz hasarına %30 direnç.',
        descriptionKey: 'legendary.buz_yuzuk.effect2',
        value: 30,
      },
    ],
    flavorText: 'Kışın soğuğu kalbinde, düşmanın kanında.',
    flavorTextKey: 'legendary.buz_yuzuk.flavor',
  },
];

// ========================
// Legendary Weapons (Extended)
// ========================

export const LEGENDARY_WEAPONS_EXT: LegendaryItem[] = [
  // Bozkurt Kılıcı - Wolf Sword (Alp)
  {
    uniqueId: 'legend_bozkurt_kilici',
    id: '',
    name: 'Bozkurt Kılıcı',
    icon: `${S7}/W_Sword019.png`,
    slot: 'weapon',
    level: 35,
    rarity: 'legendary',
    implicit: [{ type: 'damage', value: 55, tier: 5 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'damage', value: 40, tier: 4 },
      { type: 'attackSpeed', value: 20, tier: 3 },
      { type: 'critChance', value: 12, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_hit',
        effectId: 'wolf_fang',
        description: 'Vuruşlarda %20 şans ile kurt dişi etkisi: düşmanlar kanar ve yavaşlar.',
        descriptionKey: 'legendary.bozkurt_kilici.effect1',
        value: 25,
        chance: 20,
        cooldown: 1.5,
      },
      {
        type: 'on_kill',
        effectId: 'wolf_spirit',
        description: 'Öldürmelerde kurt ruhu çağırır, 5 saniye yakındaki düşmanlara hasar verir.',
        descriptionKey: 'legendary.bozkurt_kilici.effect2',
        value: 30,
        duration: 5,
        cooldown: 10,
      },
    ],
    flavorText: 'Bozkurt sürüsünün lideri gibi savaş, düşmanlarını parçala.',
    flavorTextKey: 'legendary.bozkurt_kilici.flavor',
    classRestriction: ['alp'],
  },
  // Tengri'nin Gazabı - Divine Wrath (Kam)
  {
    uniqueId: 'legend_tengri_gazabi',
    id: '',
    name: "Tengri'nin Gazabı",
    icon: `${S7}/W_Staff06.png`,
    slot: 'weapon',
    level: 50,
    rarity: 'legendary',
    implicit: [{ type: 'spellDamage', value: 80, tier: 6 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'spellDamage', value: 60, tier: 5 },
      { type: 'maxMana', value: 120, tier: 5 },
      { type: 'spirit', value: 20, tier: 4 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_skill_use',
        effectId: 'divine_wrath',
        description: 'Beceri kullanımlarında %30 şans ile gökten yıldırım yağmuru yağar.',
        descriptionKey: 'legendary.tengri_gazabi.effect1',
        value: 100,
        chance: 30,
        cooldown: 4,
      },
      {
        type: 'passive',
        effectId: 'divine_fury',
        description: 'Elemental hasar %20 artar. Mana doluyken ek %15 hasar.',
        descriptionKey: 'legendary.tengri_gazabi.effect2',
        value: 20,
      },
    ],
    flavorText: 'Gök Tanrı gazaba geldiğinde, yeryüzü titrer.',
    flavorTextKey: 'legendary.tengri_gazabi.flavor',
    classRestriction: ['kam'],
  },
  // Rüzgar Bıçağı - Wind Blade (Mergen)
  {
    uniqueId: 'legend_ruzgar_bicagi',
    id: '',
    name: 'Rüzgar Bıçağı',
    icon: `${S7}/W_Dagger021.png`,
    slot: 'weapon',
    level: 40,
    rarity: 'legendary',
    implicit: [
      { type: 'damage', value: 45, tier: 5 },
      { type: 'attackSpeed', value: 30, tier: 4 },
    ],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'damage', value: 30, tier: 4 },
      { type: 'critChance', value: 18, tier: 4 },
      { type: 'moveSpeed', value: 12, tier: 3 },
      { type: 'agility', value: 15, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_hit',
        effectId: 'wind_slash',
        description: 'Her 3. vuruşta rüzgar bıçağı fırlatır, çizgi üzerindeki düşmanlara hasar verir.',
        descriptionKey: 'legendary.ruzgar_bicagi.effect1',
        value: 60,
        chance: 100,
        cooldown: 1,
      },
      {
        type: 'passive',
        effectId: 'wind_affinity',
        description: 'Hareket hızı %20 artar, saldırı hızı %10 artar.',
        descriptionKey: 'legendary.ruzgar_bicagi.effect2',
        value: 20,
      },
    ],
    flavorText: 'Rüzgarın kendisi bile bu bıçaktan kaçamaz.',
    flavorTextKey: 'legendary.ruzgar_bicagi.flavor',
    classRestriction: ['mergen'],
  },
];

// ========================
// Legendary Armor (Extended)
// ========================

export const LEGENDARY_ARMOR_EXT: LegendaryItem[] = [
  // Demir Derisi - Iron Skin (Chest)
  {
    uniqueId: 'legend_demir_derisi',
    id: '',
    name: 'Demir Derisi',
    icon: `${S7}/A_Armour03.png`,
    slot: 'chest',
    level: 40,
    rarity: 'legendary',
    implicit: [{ type: 'armor', value: 100, tier: 6 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'maxHp', value: 120, tier: 5 },
      { type: 'armor', value: 80, tier: 5 },
      { type: 'strength', value: 15, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_damaged',
        effectId: 'iron_skin_reflect',
        description: 'Hasar aldığında %15 şans ile hasarın %30\'unu saldırgana yansıtır.',
        descriptionKey: 'legendary.demir_derisi.effect1',
        value: 30,
        chance: 15,
      },
      {
        type: 'passive',
        effectId: 'iron_constitution',
        description: 'Fiziksel hasar azaltma %15 artar. Zırh etkinliği %10 artar.',
        descriptionKey: 'legendary.demir_derisi.effect2',
        value: 15,
      },
    ],
    flavorText: 'Derisi demirden, kalbi çelikten.',
    flavorTextKey: 'legendary.demir_derisi.flavor',
  },
  // Şaman Cübbesi - Shaman Robe (Chest)
  {
    uniqueId: 'legend_saman_cubbesi',
    id: '',
    name: 'Şaman Cübbesi',
    icon: `${S7}/A_Clothing02.png`,
    slot: 'chest',
    level: 35,
    rarity: 'legendary',
    implicit: [{ type: 'energyShield', value: 80, tier: 5 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'maxMana', value: 100, tier: 5 },
      { type: 'energyShield', value: 60, tier: 4 },
      { type: 'spirit', value: 18, tier: 4 },
      { type: 'allResist', value: 12, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'passive',
        effectId: 'spirit_ward',
        description: 'Enerji kalkanı yenilenmesi %50 artar. Mana yenilenmesi %20 artar.',
        descriptionKey: 'legendary.saman_cubbesi.effect1',
        value: 50,
      },
      {
        type: 'on_skill_use',
        effectId: 'ancestral_echo',
        description: 'Beceri kullanımlarında %10 şans ile ata ruhları koruyucu aura oluşturur.',
        descriptionKey: 'legendary.saman_cubbesi.effect2',
        value: 100,
        chance: 10,
        cooldown: 15,
        duration: 5,
      },
    ],
    flavorText: 'Ataların bilgeliği her dikişine işlenmiş.',
    flavorTextKey: 'legendary.saman_cubbesi.flavor',
    classRestriction: ['kam'],
  },
  // Kurt Başlığı - Wolf Helm (Head)
  {
    uniqueId: 'legend_kurt_basligi',
    id: '',
    name: 'Kurt Başlığı',
    icon: `${S7}/C_Elm04.png`,
    slot: 'head',
    level: 35,
    rarity: 'legendary',
    implicit: [{ type: 'armor', value: 45, tier: 5 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'maxHp', value: 70, tier: 4 },
      { type: 'strength', value: 12, tier: 3 },
      { type: 'critChance', value: 8, tier: 2 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_hit',
        effectId: 'wolf_howl_helm',
        description: 'Vuruşlarda %10 şans ile kurt ulur, yakındaki düşmanları 2 saniye korkutur.',
        descriptionKey: 'legendary.kurt_basligi.effect1',
        value: 2,
        chance: 10,
        cooldown: 8,
        duration: 2,
      },
      {
        type: 'passive',
        effectId: 'wolf_senses',
        description: 'Kritik şansı %5 artar. Görüş mesafesi artar.',
        descriptionKey: 'legendary.kurt_basligi.effect2',
        value: 5,
      },
    ],
    flavorText: 'Kurt gibi gör, kurt gibi koku al, kurt gibi öldür.',
    flavorTextKey: 'legendary.kurt_basligi.flavor',
    classRestriction: ['alp'],
  },
  // Tengri Tacı - Divine Crown (Head)
  {
    uniqueId: 'legend_tengri_taci',
    id: '',
    name: 'Tengri Tacı',
    icon: `${S7}/C_Elm04.png`,
    slot: 'head',
    level: 45,
    rarity: 'legendary',
    implicit: [{ type: 'energyShield', value: 50, tier: 5 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'maxMana', value: 80, tier: 5 },
      { type: 'spirit', value: 20, tier: 4 },
      { type: 'allResist', value: 15, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'passive',
        effectId: 'divine_wisdom',
        description: 'Tüm elemental hasar %15 artar. Tecrübe kazanımı %20 artar.',
        descriptionKey: 'legendary.tengri_taci.effect1',
        value: 15,
      },
      {
        type: 'on_damaged',
        effectId: 'divine_intervention',
        description: 'Ölümcül hasar aldığında %25 şans ile hasar tamamen engellenir.',
        descriptionKey: 'legendary.tengri_taci.effect2',
        value: 100,
        chance: 25,
        cooldown: 90,
      },
    ],
    flavorText: 'Gök Tanrı\'nın tacını giyen, ilahi koruma altına girer.',
    flavorTextKey: 'legendary.tengri_taci.flavor',
    classRestriction: ['kam'],
  },
  // Ateş Eldiveni - Fire Gauntlets (Gloves)
  {
    uniqueId: 'legend_ates_eldiveni',
    id: '',
    name: 'Ateş Eldiveni',
    icon: `${S7}/W_Fist005.png`,
    slot: 'gloves',
    level: 35,
    rarity: 'legendary',
    implicit: [{ type: 'attackSpeed', value: 15, tier: 4 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'damage', value: 25, tier: 3 },
      { type: 'attackSpeed', value: 12, tier: 3 },
      { type: 'strength', value: 10, tier: 2 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_hit',
        effectId: 'flame_touch',
        description: 'Vuruşlar düşmanı yakar. %25 şans ile ateş patlaması (çevredeki düşmanlara hasar).',
        descriptionKey: 'legendary.ates_eldiveni.effect1',
        value: 40,
        chance: 25,
        cooldown: 2,
      },
      {
        type: 'passive',
        effectId: 'fire_mastery',
        description: 'Ateş hasarı %20 artar. Saldırı hızı %8 artar.',
        descriptionKey: 'legendary.ates_eldiveni.effect2',
        value: 20,
      },
    ],
    flavorText: 'Ateşe dokunan yanar, ama bu eldivenlerle ateş senin silahın olur.',
    flavorTextKey: 'legendary.ates_eldiveni.flavor',
  },
  // Fırtına Pençesi - Storm Claws (Gloves)
  {
    uniqueId: 'legend_firtina_pencesi',
    id: '',
    name: 'Fırtına Pençesi',
    icon: `${S7}/W_Fist004.png`,
    slot: 'gloves',
    level: 40,
    rarity: 'legendary',
    implicit: [{ type: 'attackSpeed', value: 18, tier: 4 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'spellDamage', value: 30, tier: 4 },
      { type: 'attackSpeed', value: 15, tier: 3 },
      { type: 'agility', value: 12, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_hit',
        effectId: 'chain_lightning_gloves',
        description: 'Vuruşlarda %20 şans ile zincir şimşek fırlatır, 3 düşmana zıplar.',
        descriptionKey: 'legendary.firtina_pencesi.effect1',
        value: 35,
        chance: 20,
        cooldown: 2,
      },
      {
        type: 'passive',
        effectId: 'storm_power',
        description: 'Şimşek hasarı %15 artar. Saldırı hızı %10 artar.',
        descriptionKey: 'legendary.firtina_pencesi.effect2',
        value: 15,
      },
    ],
    flavorText: 'Parmaklarından şimşek akar, düşmanlarını parçalar.',
    flavorTextKey: 'legendary.firtina_pencesi.flavor',
  },
  // Savaşçı Kemeri - Warrior Belt (Belt)
  {
    uniqueId: 'legend_savasci_kemeri',
    id: '',
    name: 'Savaşçı Kemeri',
    icon: `${S7}/A_Armor05.png`,
    slot: 'belt',
    level: 40,
    rarity: 'legendary',
    implicit: [{ type: 'maxHp', value: 60, tier: 5 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'maxHp', value: 100, tier: 5 },
      { type: 'strength', value: 15, tier: 3 },
      { type: 'armor', value: 40, tier: 3 },
      { type: 'damage', value: 15, tier: 3, calcType: 'increased' },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_kill',
        effectId: 'warrior_fury',
        description: 'Öldürmelerde 5 saniye boyunca hasar %15 ve saldırı hızı %10 artar. Yığılır.',
        descriptionKey: 'legendary.savasci_kemeri.effect1',
        value: 15,
        duration: 5,
      },
      {
        type: 'passive',
        effectId: 'warrior_endurance',
        description: 'Can %50 altındayken zırh %25 artar.',
        descriptionKey: 'legendary.savasci_kemeri.effect2',
        value: 25,
      },
    ],
    flavorText: 'Bu kemeri takan savaşçı, yenilmeyi unutur.',
    flavorTextKey: 'legendary.savasci_kemeri.flavor',
    classRestriction: ['alp'],
  },
  // Ejder Kalkanı - Dragon Shield (Offhand)
  {
    uniqueId: 'legend_ejder_kalkani',
    id: '',
    name: 'Ejder Kalkanı',
    icon: `${DB32}/shield_gold.png`,
    slot: 'offhand',
    level: 40,
    rarity: 'legendary',
    implicit: [{ type: 'armor', value: 70, tier: 5 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'armor', value: 60, tier: 5 },
      { type: 'maxHp', value: 80, tier: 4 },
      { type: 'allResist', value: 18, tier: 4 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_damaged',
        effectId: 'dragon_breath',
        description: 'Blok yaptığında %30 şans ile ejder nefesi fırlatır, ön tarafa ateş hasarı verir.',
        descriptionKey: 'legendary.ejder_kalkani.effect1',
        value: 60,
        chance: 30,
        cooldown: 3,
      },
      {
        type: 'passive',
        effectId: 'dragon_scales',
        description: 'Tüm dirençler %10 artar. Zırh %15 artar.',
        descriptionKey: 'legendary.ejder_kalkani.effect2',
        value: 10,
      },
    ],
    flavorText: 'Ejderin pullarından yapılmış, ateşe bile dayanır.',
    flavorTextKey: 'legendary.ejder_kalkani.flavor',
  },
  // Gölge Çizmesi - Shadow Boots (Boots)
  {
    uniqueId: 'legend_golge_cizmesi',
    id: '',
    name: 'Gölge Çizmesi',
    icon: `${S7}/A_Shoes07.png`,
    slot: 'boots',
    level: 35,
    rarity: 'legendary',
    implicit: [{ type: 'moveSpeed', value: 25, tier: 5 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'moveSpeed', value: 18, tier: 4 },
      { type: 'evasion', value: 45, tier: 4 },
      { type: 'agility', value: 14, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_damaged',
        effectId: 'shadow_dash',
        description: 'Hasar aldığında %20 şans ile gölgeye karışır, 1 saniye dokunulmaz olur.',
        descriptionKey: 'legendary.golge_cizmesi.effect1',
        value: 1,
        chance: 20,
        cooldown: 12,
        duration: 1,
      },
      {
        type: 'passive',
        effectId: 'silent_step',
        description: 'Hareket hızı %15 artar. Düşmanlar daha geç fark eder.',
        descriptionKey: 'legendary.golge_cizmesi.effect2',
        value: 15,
      },
    ],
    flavorText: 'Gölgeler arasında kaybolmak isteyenler için.',
    flavorTextKey: 'legendary.golge_cizmesi.flavor',
    classRestriction: ['mergen'],
  },
];

// ========================
// Legendary Accessories (Extended)
// ========================

export const LEGENDARY_ACCESSORIES_EXT: LegendaryItem[] = [
  // Kam Gerdanlığı - Shaman Necklace (Amulet)
  {
    uniqueId: 'legend_kam_gerdanligi',
    id: '',
    name: 'Kam Gerdanlığı',
    icon: `${S7}/Ac_Necklace06.png`,
    slot: 'amulet',
    level: 35,
    rarity: 'legendary',
    implicit: [{ type: 'allResist', value: 18, tier: 4 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'spellDamage', value: 30, tier: 4 },
      { type: 'maxMana', value: 60, tier: 4 },
      { type: 'spirit', value: 14, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_skill_use',
        effectId: 'spirit_surge',
        description: 'Beceri kullanımlarında %15 şans ile mana maliyeti geri ödenir.',
        descriptionKey: 'legendary.kam_gerdanligi.effect1',
        value: 100,
        chance: 15,
      },
      {
        type: 'passive',
        effectId: 'shaman_bond',
        description: 'Büyü hasarı %12 artar. Mana yenilenmesi %25 artar.',
        descriptionKey: 'legendary.kam_gerdanligi.effect2',
        value: 12,
      },
    ],
    flavorText: 'Şamanların gizli bilgeliği bu gerdanlıkta saklı.',
    flavorTextKey: 'legendary.kam_gerdanligi.flavor',
    classRestriction: ['kam'],
  },
  // Kut Yüzüğü - Holy Ring (Ring)
  {
    uniqueId: 'legend_kut_yuzugu',
    id: '',
    name: 'Kut Yüzüğü',
    icon: `${S7}/Ac_Ring02.png`,
    slot: 'ring1',
    level: 40,
    rarity: 'legendary',
    implicit: [],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'maxHp', value: 50, tier: 4 },
      { type: 'maxMana', value: 40, tier: 3 },
      { type: 'allResist', value: 10, tier: 3 },
      { type: 'spirit', value: 8, tier: 2 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'passive',
        effectId: 'holy_blessing',
        description: 'Can ve mana yenilenmesi %30 artar.',
        descriptionKey: 'legendary.kut_yuzugu.effect1',
        value: 30,
      },
      {
        type: 'on_damaged',
        effectId: 'holy_shield',
        description: 'Can %25 altına düştüğünde kutsal kalkan oluşur, 3 saniye hasar emilir.',
        descriptionKey: 'legendary.kut_yuzugu.effect2',
        value: 150,
        duration: 3,
        cooldown: 60,
      },
    ],
    flavorText: 'Tanrısal kutsama parmağında, koruma kalbinde.',
    flavorTextKey: 'legendary.kut_yuzugu.flavor',
  },
  // Od Mührü - Fire Seal (Ring)
  {
    uniqueId: 'legend_od_muhru',
    id: '',
    name: 'Od Mührü',
    icon: `${S7}/Ac_Ring02.png`,
    slot: 'ring1',
    level: 35,
    rarity: 'legendary',
    implicit: [],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'damage', value: 25, tier: 4 },
      { type: 'spellDamage', value: 30, tier: 4 },
      { type: 'critChance', value: 12, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_hit',
        effectId: 'fire_seal_burn',
        description: 'Vuruşlarda %30 şans ile düşman etrafında ateş halkası oluşur.',
        descriptionKey: 'legendary.od_muhru.effect1',
        value: 45,
        chance: 30,
        cooldown: 2,
      },
      {
        type: 'on_kill',
        effectId: 'fire_seal_explosion',
        description: 'Öldürmelerde düşman patlar, çevredeki düşmanlara ateş hasarı verir.',
        descriptionKey: 'legendary.od_muhru.effect2',
        value: 60,
      },
    ],
    flavorText: 'Od ateşi parmağında, düşmanlarının sonu cehennemde.',
    flavorTextKey: 'legendary.od_muhru.flavor',
  },
  // Gölge Pelerini - Shadow Cape (Cape)
  {
    uniqueId: 'legend_golge_pelerini_cape',
    id: '',
    name: 'Gölge Pelerini',
    icon: `${S7}/A_Armour02.png`,
    slot: 'cape',
    level: 35,
    rarity: 'legendary',
    implicit: [{ type: 'evasion', value: 40, tier: 4 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'evasion', value: 50, tier: 4 },
      { type: 'moveSpeed', value: 12, tier: 3 },
      { type: 'agility', value: 12, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_damaged',
        effectId: 'shadow_cloak',
        description: '%15 şans ile gölgeye bürünür, 2 saniye görünmez olur (hasar almaz).',
        descriptionKey: 'legendary.golge_pelerini_cape.effect1',
        value: 2,
        chance: 15,
        cooldown: 15,
        duration: 2,
      },
      {
        type: 'passive',
        effectId: 'shadow_affinity',
        description: 'Kaçış %20 artar. Hareket hızı %8 artar.',
        descriptionKey: 'legendary.golge_pelerini_cape.effect2',
        value: 20,
      },
    ],
    flavorText: 'Gölgeler seni sarar, düşmanlarını şaşırtır.',
    flavorTextKey: 'legendary.golge_pelerini_cape.flavor',
  },
  // Ateş Kuzgun Pelerini - Fire Raven Cape
  {
    uniqueId: 'legend_ates_kuzgun_pelerini',
    id: '',
    name: 'Ateş Kuzgun Pelerini',
    icon: `${S7}/A_Armour02.png`,
    slot: 'cape',
    level: 40,
    rarity: 'legendary',
    implicit: [{ type: 'armor', value: 30, tier: 3 }, { type: 'evasion', value: 20, tier: 3 }],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'damage', value: 20, tier: 3, calcType: 'increased' },
      { type: 'maxHp', value: 50, tier: 3 },
      { type: 'critChance', value: 8, tier: 2 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_kill',
        effectId: 'raven_fire',
        description: 'Öldürmelerde ateş kuzgunları çağırır, 3 saniye boyunca düşmanlara hasar verir.',
        descriptionKey: 'legendary.ates_kuzgun_pelerini.effect1',
        value: 25,
        duration: 3,
        cooldown: 8,
      },
      {
        type: 'passive',
        effectId: 'raven_sight',
        description: 'Kritik hasarı %20 artar. Ateş direnci %15 artar.',
        descriptionKey: 'legendary.ates_kuzgun_pelerini.effect2',
        value: 20,
      },
    ],
    flavorText: 'Ateş kuzgunları efendilerinin düşmanlarını asla affetmez.',
    flavorTextKey: 'legendary.ates_kuzgun_pelerini.flavor',
  },
  // Buz Kalbi Yüzüğü - Frost Heart Ring
  {
    uniqueId: 'legend_buz_kalbi',
    id: '',
    name: 'Buz Kalbi Yüzüğü',
    icon: `${S7}/Ac_Ring02.png`,
    slot: 'ring1',
    level: 45,
    rarity: 'legendary',
    implicit: [],
    prefixes: [],
    suffixes: [],
    fixedMods: [
      { type: 'spellDamage', value: 35, tier: 4 },
      { type: 'maxMana', value: 60, tier: 4 },
      { type: 'energyShield', value: 40, tier: 3 },
    ],
    enhancement: 0,
    legendaryEffects: [
      {
        type: 'on_hit',
        effectId: 'frost_nova_ring',
        description: 'Vuruşlarda %20 şans ile buz novası patlar, düşmanları yavaşlatır.',
        descriptionKey: 'legendary.buz_kalbi.effect1',
        value: 50,
        chance: 20,
        cooldown: 3,
      },
      {
        type: 'passive',
        effectId: 'frost_heart',
        description: 'Buz hasarı %25 artar. Buz direnci %20 artar.',
        descriptionKey: 'legendary.buz_kalbi.effect2',
        value: 25,
      },
    ],
    flavorText: 'Kalbi buz gibi soğuk, ama gücü daima yanında.',
    flavorTextKey: 'legendary.buz_kalbi.flavor',
  },
];

// ========================
// All Legendaries
// ========================

export const ALL_LEGENDARY_ITEMS: LegendaryItem[] = [
  ...LEGENDARY_WEAPONS,
  ...LEGENDARY_ARMOR,
  ...LEGENDARY_ACCESSORIES,
  ...LEGENDARY_WEAPONS_EXT,
  ...LEGENDARY_ARMOR_EXT,
  ...LEGENDARY_ACCESSORIES_EXT,
];

// Get legendary by unique ID
export function getLegendaryById(uniqueId: string): LegendaryItem | undefined {
  return ALL_LEGENDARY_ITEMS.find(item => item.uniqueId === uniqueId);
}

// Get legendaries by slot
export function getLegendariesBySlot(slot: string): LegendaryItem[] {
  return ALL_LEGENDARY_ITEMS.filter(item => item.slot === slot);
}

// Get legendaries available for a class
export function getLegendariesForClass(className: string): LegendaryItem[] {
  return ALL_LEGENDARY_ITEMS.filter(item =>
    !item.classRestriction || item.classRestriction.includes(className)
  );
}

// Get legendaries by minimum level
export function getLegendariesByLevel(minLevel: number, maxLevel: number): LegendaryItem[] {
  return ALL_LEGENDARY_ITEMS.filter(item =>
    item.level >= minLevel && item.level <= maxLevel
  );
}

// Create an instance of a legendary item with a unique ID
let legendaryIdCounter = 0;
export function createLegendaryInstance(uniqueId: string): LegendaryItem | null {
  const template = getLegendaryById(uniqueId);
  if (!template) return null;

  return {
    ...template,
    id: `legendary_${Date.now()}_${legendaryIdCounter++}`,
  };
}

// Roll for a random legendary drop based on level and class
export function rollLegendaryDrop(
  playerLevel: number,
  playerClass: string,
  luckBonus: number = 0
): LegendaryItem | null {
  // Base drop chance is very low
  const baseChance = 0.5;  // 0.5% base chance
  const adjustedChance = baseChance + (luckBonus * 0.1);

  if (Math.random() * 100 > adjustedChance) {
    return null;  // No legendary dropped
  }

  // Get available legendaries for this level and class
  const available = ALL_LEGENDARY_ITEMS.filter(item => {
    const levelCheck = item.level <= playerLevel + 5;  // Can drop up to 5 levels above player
    const classCheck = !item.classRestriction || item.classRestriction.includes(playerClass);
    return levelCheck && classCheck;
  });

  if (available.length === 0) return null;

  // Pick a random legendary
  const chosen = available[Math.floor(Math.random() * available.length)];
  return createLegendaryInstance(chosen.uniqueId);
}
